// The app's own pickers (mobile/ios BossChatPicker.swift). A file input opened in a web page shows iOS's Photo Library /
// Choose Files menu before the photo library, so the attach panel's Photos would be a menu that opens another menu. The app
// takes over the web view's file inputs instead: the page says which picker it wants just before opening one, and the app
// opens that picker directly. The files come back to the input as ever, so the upload is unchanged.

import { isNative, nativePlatform } from './native'

export type PickerKind = 'photos' | 'camera' | 'documents'

export interface PickerPlugin {
  next(options: { kind: PickerKind }): Promise<void>
  addListener(event: 'pickFailed', listener: (data: { count: number }) => void): Promise<{ remove: () => Promise<void> }>
}

interface CapacitorGlobal {
  isPluginAvailable?: (name: string) => boolean
}

// Functions, never the plugin proxy: an async function resolving with the proxy would call BossChatPicker.then().
let loaded: Promise<{ plugin: PickerPlugin }> | null = null
function picker(): Promise<{ plugin: PickerPlugin }> {
  loaded ??= import('@capacitor/core').then(({ registerPlugin }) => ({ plugin: registerPlugin<PickerPlugin>('BossChatPicker') }))
  return loaded
}

/** Test seam. */
export function setPickerPlugin(plugin: PickerPlugin | null): void {
  loaded = plugin ? Promise.resolve({ plugin }) : null
}

/** Whether the app has its own pickers: the iOS app from build 4. */
export function hasNativePicker(): boolean {
  if (!isNative() || nativePlatform() !== 'ios') return false
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor?.isPluginAvailable?.('BossChatPicker') === true
}

/** Loads the plugin's bridge ahead of a tap, so asking for a picker takes no time from the tap's permission to open one. */
export function preparePicker(): void {
  if (hasNativePicker()) void picker()
}

/** Asks the app to open this picker for the next file input. Best effort: never throws, and does nothing without the pickers. */
export async function askForPicker(kind: PickerKind): Promise<void> {
  if (!hasNativePicker()) return
  try {
    await (await picker()).plugin.next({ kind })
  } catch {
    // The input then opens as it would in a browser.
  }
}

/** Called with how many picked items the app couldn't load (an iCloud photo that wouldn't download). Returns a cleanup. */
export function onPickFailures(listener: (count: number) => void): () => void {
  if (!hasNativePicker()) return () => {}
  let stopped = false
  let remove: (() => Promise<void>) | null = null
  void picker()
    .then(({ plugin }) => plugin.addListener('pickFailed', ({ count }) => listener(count)))
    .then((handle) => {
      if (stopped) void handle.remove()
      else remove = handle.remove
    })
    .catch(() => {})
  return () => {
    stopped = true
    void remove?.()
  }
}
